import {ThemeIconKey, Box, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import styled from 'styled-components';

import {ChangeEvent, useId} from 'react';

import {defaultTo} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import {FormControlProps} from '../../types/FormControlProps';
import {getDataAttribute} from '../../utils/getDataAttribute';
import {HelperText} from '../HelperText/HelperText';
import {CheckboxPropsBase} from './types';

export interface CheckboxProps extends CheckboxPropsBase, FormControlProps<boolean>, TestIdProps {
  isIndeterminate?: boolean;
  indeterminateIcon?: ThemeIconKey;
  // A unique ID for the input element, allowing it to be associated with a <label> element in another component.
  externalLabelId?: string;
  'aria-label'?: string;
}

export function Checkbox(props: CheckboxProps) {
  const id = useId();
  const indeterminateIcon = defaultTo('toggle/minus', props.indeterminateIcon);
  const checkedIcon = defaultTo('toggle/tick', props.checkedIcon);

  const isInvalid = props.isInvalid || !!props.errorMessage;
  const icon = props.isIndeterminate ? indeterminateIcon : checkedIcon;
  const shouldIconShow = props.value || props.isIndeterminate;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (props.isDisabled) {
      event.preventDefault();
      return;
    }

    props.onChange?.(!props.value, event);
  };

  const rootProps = {
    'data-disabled': getDataAttribute(props.isDisabled),
    'data-checked': getDataAttribute(props.value),
    'data-invalid': getDataAttribute(isInvalid),
  };

  const checkboxProps = {
    'data-checked': getDataAttribute(props.value && !props.isIndeterminate),
    'data-disabled': getDataAttribute(props.isDisabled),
    'data-invalid': getDataAttribute(isInvalid),
    'data-indeterminate': getDataAttribute(props.isIndeterminate && !props.value),
  };

  return (
    <VStack>
      <CheckboxWrapper {...rootProps} data-testid={suffixTestId('checkbox', props)}>
        <HStack>
          <CheckboxInputWrapper {...rootProps}>
            <CheckboxInput
              id={props.externalLabelId ?? id}
              type="checkbox"
              checked={props.value}
              onChange={handleChange}
              onFocus={props.onFocus}
              onBlur={props.onBlur}
              disabled={props.isDisabled}
              required={props.isRequired}
              aria-checked={props.isIndeterminate ? 'mixed' : props.value}
              aria-label={props['aria-label']}
              aria-labelledby={
                props['aria-label'] ? undefined : `${props.externalLabelId ?? id}-label`
              }
              aria-invalid={isInvalid || undefined}
              data-testid={suffixTestId('checkboxInput', props)}
            />
            <CheckboxControl
              {...checkboxProps}
              data-testid={suffixTestId('checkboxControl', props)}
            >
              <Show when={shouldIconShow}>
                <Box flex={0} padding={0}>
                  <Icon value={icon} size={3} />
                </Box>
              </Show>
            </CheckboxControl>
          </CheckboxInputWrapper>
          <Show when={props.label}>
            <Box paddingLeft={3}>
              <Show when={props.label}>
                <Text
                  size="small"
                  id={`${id}-label`}
                  data-testid={suffixTestId('checkboxLabel', props)}
                >
                  {props.label}
                </Text>
              </Show>
              <Show when={props.helperText}>
                <HelperText
                  helperText={props.helperText}
                  data-testid={suffixTestId('helper', props)}
                />
              </Show>
              <Show when={props.errorMessage}>
                <HelperText
                  errorMessage={props.errorMessage}
                  data-testid={suffixTestId('errorMessage', props)}
                />
              </Show>
            </Box>
          </Show>
        </HStack>
      </CheckboxWrapper>
    </VStack>
  );
}

const CheckboxWrapper = styled.label`
  position: relative;
  cursor: pointer;

  &[data-disabled='true'] {
    cursor: not-allowed;
  }
`;

const CheckboxInputWrapper = styled.div`
  position: relative;
  display: inline-flex;

  &[data-disabled='true'] {
    opacity: 0.4;
  }
`;

const CheckboxInput = styled.input`
  position: absolute;
  opacity: 0;
  width: ${({theme}) => theme.getSize(5)};
  height: ${({theme}) => theme.getSize(5)};
  cursor: pointer;
  z-index: 1;

  &:disabled {
    cursor: not-allowed;
  }

  &:focus + span {
    box-shadow: 0 0 0 ${({theme}) => theme.getSize(1)}
      ${({theme}) => theme.colors.palettes.blue[30][100]};
  }

  &:hover:not(:disabled) + span {
    box-shadow: 0 0 0 ${({theme}) => theme.getSize(1)}
      ${({theme}) => theme.colors.palettes.blue[20][100]};
  }

  &:active:not(:disabled) + span {
    box-shadow: 0 0 0 ${({theme}) => theme.getSize(1)}
      ${({theme}) => theme.colors.palettes.blue[30][100]};
  }
`;

const CheckboxControl = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  user-select: none;
  width: ${({theme}) => theme.getSize(5)};
  height: ${({theme}) => theme.getSize(5)};
  background-color: ${({theme}) => theme.colors.palettes.white[10][100]};
  color: ${({theme}) => theme.colors.palettes.white[10][100]};
  border: 1px solid;
  border-color: ${({theme}) => theme.colors.palettes.neutral[200][100]};
  border-radius: ${({theme}) => theme.radii.small};
  pointer-events: none;

  &[data-invalid='true'] {
    border-color: ${({theme}) => theme.colors.severity.danger};
  }

  &[data-checked='true'] {
    background-color: ${({theme}) => theme.colors.general.accent};
    border-color: ${({theme}) => theme.colors.general.accent};
  }

  &[data-indeterminate='true'] {
    background-color: ${({theme}) => theme.colors.palettes.neutral[70][100]};
    border-color: ${({theme}) => theme.colors.general.transparent};
  }
`;
